import { toast } from "@/components/common/Toast"
import { TranslationKeys } from "@/hooks/useTranslation/useTranslation"
import { Chat } from "@/types/assistantChat"
import { downloadBlob } from "@/utils/downloadUtils"
import { useCallback } from "react"
import { exportChatAsPdf, renderChatSessionHtml } from "../utils/chatPdf"
import {
  convertChatMessagesToTextFormat,
  createEmptyChat,
  resolveChatTitle,
} from "../utils/chatUtils"

const CHAT_PDF_LANG_MAP: Record<string, string> = {
  EN: "en",
  DE: "de",
  JA: "ja",
}

interface UseChatActionsProps {
  chat: Chat
  setChat: React.Dispatch<React.SetStateAction<Chat>>
  translate: (
    key: TranslationKeys,
    dynamicValues?: Record<string, string | number>,
  ) => string
  currentLanguage: string
  onStartNewChat?: () => void
}

/**
 * Custom hook for chat actions (export, copy, new chat)
 */
export function useChatActions({
  chat,
  setChat,
  translate,
  currentLanguage,
  onStartNewChat,
}: UseChatActionsProps) {
  const exportChat = useCallback(() => {
    downloadBlob(convertChatMessagesToTextFormat(chat) ?? "")
    toast.success(translate("AI_CHAT/EXPORT"))
  }, [chat, translate])

  const resolveTitleAndDate = useCallback(() => {
    const title = resolveChatTitle(chat, () => translate("AI_CHAT/PDF_TITLE"))
    const dateLabel = new Date().toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    })
    return { title, dateLabel }
  }, [chat, translate])

  const exportChatPdf = useCallback(() => {
    try {
      const { title, dateLabel } = resolveTitleAndDate()
      const html = renderChatSessionHtml(chat, {
        title,
        dateLabel,
        sourcesLabel: translate("AI_CHAT/PDF_SOURCES"),
        pageLabel: translate("AI_CHAT/PDF_PAGE"),
        userLabel: translate("AI_CHAT/USER"),
        assistantLabel: translate("AI_CHAT/ASSISTANT"),
        unknownSourceLabel: translate("AI_CHAT/UNKNOWN_SOURCE"),
        lang: CHAT_PDF_LANG_MAP[currentLanguage] ?? "de",
      })
      exportChatAsPdf(html)
      toast.success(translate("AI_CHAT/EXPORT"))
    } catch {
      toast.error(translate("AI_CHAT/EXPORT_ERROR"))
    }
  }, [chat, translate, currentLanguage, resolveTitleAndDate])

  const copyToClipboard = useCallback(() => {
    navigator.clipboard.writeText(convertChatMessagesToTextFormat(chat) ?? "")
    toast.success(translate("AI_CHAT/CLIPBOARD"))
  }, [chat, translate])

  const startNewChat = useCallback(() => {
    onStartNewChat?.()
    setChat(createEmptyChat())
  }, [onStartNewChat, setChat])

  return {
    exportChat,
    exportChatPdf,
    copyToClipboard,
    startNewChat,
  }
}
